import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Card, Alert, Spinner, Badge } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { BASE_API_URL, BASE_PUBLIC_API_ROUTE, BASE_SECURE_ROUTE } from "src/constants/api";
import { useAxiosPoster, useAxiosPublicPost } from "src/hooks/useAxios";
import {
    getApiHeaders,
    getApiUrl,
    IConversion,
    ILocationInfo,
} from "src/app-telehealth-demo/util/utils";
import { STATE_OPTIONS } from "src/constants/regions";

interface LocationStepProps {
    locationInfo: Partial<ILocationInfo>;
    updateLocationInfo: (data: Partial<ILocationInfo>) => void;
    conversion: IConversion | null;
    updateConversion: (data: IConversion | null) => void;
    selectedServiceIds: number[];
    updateSelectedServiceIds: (ids: number[]) => void;
    onContinue: () => void;
    onBack: () => void;
}

// Form validation schema
const schema = yup.object().shape({
    address: yup.string().required("Street address is required"),
    city: yup.string().required("City is required"),
    state: yup.string().required("State is required"),
    zip: yup
        .string()
        .matches(/^[0-9]{5}(-[0-9]{4})?$/, "ZIP code must be 5 digits or 5+4 format")
        .required("ZIP code is required"),
});

const LocationStep: React.FC<LocationStepProps> = ({
    locationInfo,
    updateLocationInfo,
    conversion,
    updateConversion,
    selectedServiceIds,
    updateSelectedServiceIds,
    onContinue,
    onBack,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm({
        resolver: yupResolver(schema),
        mode: "onBlur",
        defaultValues: {
            address: locationInfo.address || "",
            city: locationInfo.city || "",
            state: locationInfo.state || "",
            zip: locationInfo.zip || "",
        },
    });

    const toggleService = (serviceId: number) => {
        if (selectedServiceIds.includes(serviceId)) {
            updateSelectedServiceIds(selectedServiceIds.filter((id) => id !== serviceId));
        } else {
            updateSelectedServiceIds([...selectedServiceIds, serviceId]);
        }
    };

    useEffect(() => {
        const subscription = watch((value) => {
            updateLocationInfo({
                address: value.address,
                city: value.city,
                state: value.state,
                zip: value.zip,
            });
        });
        return () => subscription.unsubscribe();
    }, [watch, updateLocationInfo]);

    const onSubmit = async (data: any) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await axios.post(
                getApiUrl("create-by-address"),
                {
                    address: data.address,
                    city: data.city,
                    state: data.state,
                    zip: data.zip,
                },
                {
                    headers: getApiHeaders(),
                },
            );
            const conversionData = response.data;
            updateConversion(conversionData);
        } catch (err) {
            console.error("Error verifying location:", err);
            setError(
                "We couldn't find a location near you. Please check your address or try a different one.",
            );
            updateConversion(null);
        } finally {
            setIsLoading(false);
        }
    };

    // Format currency for display
    const formatCurrency = (cents: number) => {
        return `$${(cents / 100).toFixed(2)}`;
    };

    const calculateTotal = () => {
        if (!conversion) return 0;

        return conversion.services
            .filter((service) => selectedServiceIds.includes(service.careServiceId))
            .reduce((total, service) => total + service.costInCents, 0);
    };

    return (
        <div className="location-step">
            <h2 className="mb-4">Find Services Near You</h2>
            <p className="text-muted mb-4">
                Please provide your address so we can find available services at the nearest
                location.
            </p>

            {error && <Alert variant="danger">{error}</Alert>}

            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                <Card className="mb-4">
                    <Card.Body>
                        <Card.Title>Your Location</Card.Title>

                        <Form.Group className="mb-3">
                            <Form.Label>Street Address</Form.Label>
                            <Form.Control
                                type="text"
                                isInvalid={!!errors.address}
                                {...register("address")}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.address?.message}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Row>
                            <Col md={5}>
                                <Form.Group className="mb-3">
                                    <Form.Label>City</Form.Label>
                                    <Form.Control
                                        type="text"
                                        isInvalid={!!errors.city}
                                        {...register("city")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.city?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>State</Form.Label>
                                    <Form.Select isInvalid={!!errors.state} {...register("state")}>
                                        <option value="">Select State</option>
                                        {STATE_OPTIONS.filter((option) => option.value).map(
                                            (state) => (
                                                <option key={state.value} value={state.value}>
                                                    {state.label}
                                                </option>
                                            ),
                                        )}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.state?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>ZIP Code</Form.Label>
                                    <Form.Control
                                        type="text"
                                        isInvalid={!!errors.zip}
                                        {...register("zip")}
                                        placeholder="12345"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.zip?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-grid">
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={isLoading}
                                className="mt-3"
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Checking location...
                                    </>
                                ) : conversion ? (
                                    "Update Location"
                                ) : (
                                    "Check Availability"
                                )}
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </Form>

            {conversion && (
                <Card className="mb-4 success-card border-success">
                    <Card.Body>
                        <div className="text-success mb-3">
                            <i className="bi bi-check-circle-fill me-2"></i>
                            <strong>Location Found!</strong>
                        </div>

                        <Card.Title>{conversion.locationName}</Card.Title>
                        <p className="mb-3">{conversion.locationAddress.fullAddress}</p>

                        <div className="d-flex mb-4">
                            {conversion.practitionerImageUrl && (
                                <div className="practitioner-image me-3">
                                    <img
                                        src={conversion.practitionerImageUrl}
                                        alt={conversion.practitionerName}
                                        className="rounded-circle"
                                        width="60"
                                        height="60"
                                    />
                                </div>
                            )}
                            <div>
                                <h5 className="mb-1">Your Provider</h5>
                                <p className="mb-0">{conversion.practitionerName}</p>
                            </div>
                        </div>

                        <h5 className="mb-3">Select Services</h5>
                        {conversion?.services?.map((service) => (
                            <div
                                key={service.careServiceId}
                                className="service-item mb-3 p-3 border rounded"
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <Form.Check
                                            type="checkbox"
                                            id={`service-${service.careServiceId}`}
                                            className="me-3"
                                            checked={selectedServiceIds.includes(
                                                service.careServiceId,
                                            )}
                                            onChange={() => toggleService(service.careServiceId)}
                                            label=""
                                        />
                                        <div>
                                            <h6 className="mb-1">{service.careServiceName}</h6>
                                            <div>
                                                <span className="price">
                                                    {formatCurrency(service.costInCents)}
                                                </span>
                                                {service.requiresLab && (
                                                    <Badge bg="info" className="ms-2">
                                                        Requires Lab
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {selectedServiceIds.length > 0 && (
                            <div className="mt-4 p-3 bg-light rounded">
                                <div className="d-flex justify-content-between">
                                    <h5 className="mb-0">Total:</h5>
                                    <h5 className="mb-0">{formatCurrency(calculateTotal())}</h5>
                                </div>
                            </div>
                        )}

                        <div className="mt-4">
                            <p className="mb-2">
                                <small className="text-muted">
                                    Tracking ID: {conversion.conversionTrackingUuid}
                                </small>
                            </p>
                        </div>
                    </Card.Body>
                </Card>
            )}

            <div className="d-flex justify-content-between mt-4">
                <Button variant="outline-secondary" onClick={onBack}>
                    Back
                </Button>
                <Button
                    variant="primary"
                    onClick={onContinue}
                    disabled={!conversion || selectedServiceIds.length === 0}
                >
                    {!conversion
                        ? "Please find a location first"
                        : selectedServiceIds.length === 0
                          ? "Please select at least one service"
                          : "Continue"}
                </Button>
            </div>
        </div>
    );
};

export default LocationStep;
